import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, BadgeCheck, Check, ChevronDown, ShieldCheck, TrendingUp, FileText, Landmark, IndianRupee } from "lucide-react";
import { ASSETS, BRAND, FAQS, SERVICES, TESTIMONIALS, PLATFORMS, waLink } from "@/data/content";
import Tools from "@/components/Tools";
import CtaBand from "@/components/CtaBand";
import MediaShowcase from "@/components/MediaShowcase";

const ICONS = { insurance: ShieldCheck, "investment-mutual-funds": TrendingUp, "tax-gst": FileText, "banking-csc": Landmark };

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const [activeFaq, setActiveFaq] = useState(0);
  return (
    <>
      <section className="hero" data-testid="hero-section">
        <div className="hero-orbit orbit-one" />
        <div className="hero-orbit orbit-two" />
        <div className="hero-copy">
          <motion.div className="eyebrow gold" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <BadgeCheck size={15} /> {BRAND.address.split(",")[0]}, Bindki
          </motion.div>
          <h1 className="hero-title">
            {["Insurance, Investment,", "Tax & GST Solutions", "in Bindki"].map((line, i) => (
              <span className="line-mask" key={line}>
                <motion.span custom={i} variants={lineReveal} initial="hidden" animate="show" className={i === 1 ? "accent" : ""}>{line}</motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}>
            Life insurance se lekar mutual funds, ITR se PAN card tak — aapki har financial zaroorat ka ek bharosemand saathi. {BRAND.tagline}.
          </motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp" data-testid="hero-whatsapp-button">WhatsApp Consultation</a>
            <a href={`tel:+91${BRAND.phone1}`} className="btn btn-primary" data-testid="hero-call-button">Call {BRAND.phone1}</a>
            <Link to="/services" className="btn btn-ghost" data-testid="hero-services-button">Explore services <ArrowUpRight size={16} /></Link>
          </motion.div>
          <motion.div className="hero-trust" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
            <div className="avatar-stack"><img src={ASSETS.profile} alt="Utkarsh Gupta" /><span>+</span></div>
            <span>Personal guidance from<br /><strong>{BRAND.advisor}</strong></span>
          </motion.div>
        </div>
        <motion.div className="hero-visual" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.3 }}>
          <div className="hero-photo"><img src={ASSETS.hero} alt="Bima Bachat Bazar - Utkarsh Gupta financial advisory Bindki" /></div>
          <div className="floating-card card-top"><span className="mini-icon green"><TrendingUp size={15} /></span><span><small>Growth mindset</small><strong>Plan • Protect • Prosper</strong></span></div>
          <div className="floating-card card-bottom"><span className="mini-icon gold"><IndianRupee size={15} /></span><span><small>Start a SIP from</small><strong>₹500 / month</strong></span></div>
        </motion.div>
      </section>

      <section className="proof-strip" data-testid="proof-strip">
        <div><strong>4+</strong><span>Service verticals</span></div>
        <div><strong>11</strong><span>Partner platforms</span></div>
        <div><strong>2</strong><span>Dedicated helplines</span></div>
        <div><strong>100%</strong><span>Personal attention</span></div>
      </section>

      <div className="marquee" data-testid="brand-marquee">
        <div className="marquee-track">
          {Array(2).fill(["Insurance", "Mutual Funds", "SIP Planning", "Tax & GST", "PAN Card", "Voter ID", "Ayushman Bharat", "AePS Banking", "Retirement Planning", "Term Insurance"]).flat().map((item, i) => (
            <span key={i}>{item} <em>✦</em></span>
          ))}
        </div>
      </div>

      <section className="section" data-testid="services-section">
        <div className="section-head">
          <div>
            <div className="eyebrow blue">What we help with</div>
            <h2>Money decisions,<br /><span className="accent">made simpler.</span></h2>
          </div>
          <p>Char core services — har ek ka apna dedicated page. Clear guidance, without the jargon.</p>
        </div>
        <div className="service-grid">
          {SERVICES.map((service, i) => {
            const Icon = ICONS[service.slug];
            return (
              <motion.article className={`service-card ${service.tone}`} key={service.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} data-testid={`service-card-${service.slug}`}>
                <span className="chapter-no">0{i + 1}</span>
                <div className={`service-icon ${service.tone}`}><Icon size={22} /></div>
                <h3>{service.title}</h3>
                <small className="service-hindi">{service.hindi}</small>
                <p>{service.text}</p>
                <Link to={`/services/${service.slug}`} data-testid={`service-link-${service.slug}`}>Explore <ArrowUpRight size={15} /></Link>
              </motion.article>
            );
          })}
        </div>
      </section>

      <CtaBand compact />

      <Tools />

      <MediaShowcase />

      <section className="section about-teaser" data-testid="about-section">
        <div className="about-image">
          <img src={ASSETS.profile} alt="Utkarsh Gupta - financial advisor Bindki" />
          <div className="image-caption"><strong>{BRAND.advisor}</strong><span>Corporate Investment Consultant</span></div>
        </div>
        <div className="about-copy">
          <div className="eyebrow gold">The person behind the plan</div>
          <h2>Advice that feels<br /><span className="accent">personal.</span></h2>
          <p className="lead">"Aapka paisa, aapke sapne — planning bhi aapke hisaab se."</p>
          <p>Bima Bachat Bazar mein financial planning approachable hoti hai. Local understanding, long-term thinking aur ek simple promise — aapko confident money decisions lene mein madad.</p>
          <div className="check-list">
            <span><Check size={15} /> Goal-first planning</span>
            <span><Check size={15} /> Transparent guidance</span>
            <span><Check size={15} /> One trusted point of contact</span>
          </div>
          <Link className="text-link" to="/about" data-testid="about-more-link">Meet your advisor <ArrowUpRight size={15} /></Link>
        </div>
      </section>

      <section className="section testimonials-section" data-testid="testimonials-section">
        <div className="section-head">
          <div>
            <div className="eyebrow gold">People we've helped</div>
            <h2>A little more<br /><span className="accent">peace of mind.</span></h2>
          </div>
          <p>Real conversations. Practical plans. Long-term relationships.</p>
        </div>
        <div className="testimonial-grid">
          {TESTIMONIALS.map((t, i) => (
            <motion.article className="testimonial-card" key={t.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }} data-testid={`testimonial-card-${i}`}>
              <div className="quote-mark">"</div>
              <p>{t.quote}</p>
              <div className="testimonial-person"><span>{t.name.split(" ").map((p) => p[0]).join("")}</span><div><strong>{t.name}</strong><small>{t.role}</small></div></div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="section platforms-strip" data-testid="platforms-strip">
        <div className="section-head centered">
          <div className="eyebrow blue">Trusted partner platforms</div>
          <h2>Plans check karein <span className="accent">seedha official links se.</span></h2>
        </div>
        <div className="platform-chips">
          {PLATFORMS.slice(0, 6).map((p) => (
            <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`home-platform-${p.name.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-")}`}>{p.name} <ArrowUpRight size={13} /></a>
          ))}
          <Link to="/connect" data-testid="home-platform-all">View all 11 platforms <ArrowUpRight size={13} /></Link>
        </div>
      </section>

      <section className="section faq-section" id="faq" data-testid="faq-section">
        <div className="faq-intro">
          <div className="eyebrow gold">Good questions deserve clear answers</div>
          <h2>Frequently<br /><span className="accent">asked.</span></h2>
          <p>Still unsure? We're only a WhatsApp message away.</p>
          <a className="text-link" href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="faq-whatsapp-button">Ask us directly <ArrowUpRight size={15} /></a>
        </div>
        <div className="faq-list">
          {FAQS.map(([q, a], i) => (
            <div className={activeFaq === i ? "faq-item active" : "faq-item"} key={q}>
              <button onClick={() => setActiveFaq(activeFaq === i ? -1 : i)} data-testid={`faq-toggle-${i}`}><span>{q}</span><ChevronDown size={17} /></button>
              {activeFaq === i && <p data-testid={`faq-answer-${i}`}>{a}</p>}
            </div>
          ))}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
